import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Typography } from '@volvo/vcdk-react';
import { useTranslation } from 'react-i18next';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import useMediaQuery from '@/hooks/useMediaQuery';

type Props<T> = {
  data: T[];
  columns: ColumnDef<T>[];
};

const CoverageTable = <T,>({ data: _data, columns: _columns }: Props<T>) => {
  const { t } = useTranslation();
  const { mediaQueryNodes: mq } = useMediaQuery();
  const data = _data;
  const columns = _columns;

  // TanStack Table returns non-memoizable functions; React Compiler intentionally skips this.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
  });

  const MobileCards = () => (
    <div className="mt-6 flex flex-col gap-6">
      {table.getRowModel().rows.map((row) => (
        <div key={row.id} className="border-subtle border-b pb-6 last:border-0">
          <div className="relative flex flex-col gap-3">
            {/* Render actions absolutely in the top-right corner */}
            {row.getVisibleCells().map((cell) => {
              if (cell.column.id === 'actions') {
                return (
                  <div key={cell.id} className="absolute top-0 right-0">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                );
              }
              return null;
            })}
            {row.getVisibleCells().map((cell) => {
              const meta = cell.column.columnDef.meta as { hideOnMobile?: boolean } | undefined;

              // Skip rendering the actions column in the card body, we'll add it separately
              if (cell.column.id === 'select') return null;
              if (cell.column.id === 'actions') return null;

              // Skip rendering if hideOnMobile is true
              if (meta?.hideOnMobile) return null;

              const headerLabel =
                typeof cell.column.columnDef.header === 'string' ? cell.column.columnDef.header : null;

              return (
                <div key={cell.id} className="flex justify-between gap-2">
                  {headerLabel && (
                    <Typography variant="caption1" className="text-subtle">
                      {headerLabel}
                    </Typography>
                  )}
                  <div className="truncate">{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  const DesktopTable = () => (
    <table className="mt-6 table-fixed">
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th key={header.id} colSpan={header.colSpan} scope="col" className="text-left">
                <Typography variant="caption2" className="text-subtle">
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </Typography>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id} className="py-3">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  if (data.length === 0) {
    return (
      <div className="flex justify-center py-10">
        <Typography variant="heading3" className="text-subtle">
          {t(($) => $.pages.unit.coverage.empty_state.title)}
        </Typography>
      </div>
    );
  }

  return mq({
    default: <MobileCards />,
    md: <DesktopTable />,
  });
};

export default withComponentErrorBoundary(CoverageTable) as typeof CoverageTable;
